import type { Exercise } from '@nelnela/contracts';

/**
 * The review run's state machine (M6a step 4) — pure, so the screen can stay a renderer and the
 * awkward parts can be held still by a test.
 *
 * The awkward parts are all about *once*: the options offer is made once per run and never
 * again; an answered prompt is answered once, however many times the button is pressed; the
 * summary counts each prompt once. Every one of those is a rule about history, which is what a
 * reducer is for and what a pile of `useState` in a screen is not.
 */

export type AnswerMode = 'typed' | 'options';

export interface RunResult {
  exerciseId: string;
  correct: boolean;
  /** how it was answered — 'shown' and 'skipped' are not failures of the same kind */
  mode: AnswerMode | 'shown' | 'skipped';
  /** did the model score it? A listen & repeat prompt is practice, and `correct: true` on one of
   *  those means "not graded" — the event carries `correct = null`. Counting it as a pass is how
   *  a run of six unscored prompts came to summarise as "6 right". */
  graded: boolean;
}

export interface RunState {
  exercises: Exercise[];
  /** which prompt is on screen; equals `exercises.length` when the run is over */
  idx: number;
  /** the mode the REST of the run is answered in, once the learner has switched */
  answerMode: AnswerMode;
  /** the offer is a one-shot: made once, then never again whatever the learner does with it */
  offerState: 'unoffered' | 'showing' | 'settled';
  results: RunResult[];
  /** the verdict on screen, between answering and advancing */
  verdict: { correct: boolean; graded: boolean; expected: unknown; explanation?: string } | null;
}

export function initRun(exercises: Exercise[], answerMode: AnswerMode = 'typed'): RunState {
  return {
    exercises,
    idx: 0,
    answerMode,
    offerState: 'unoffered',
    results: [],
    verdict: null,
  };
}

export const currentExercise = (s: RunState): Exercise | null => s.exercises[s.idx] ?? null;
export const isDone = (s: RunState): boolean => s.idx >= s.exercises.length;

/**
 * Is this character Georgian?
 *
 * The whole Georgian block (U+10A0–U+10FF: Asomtavruli, Nuskhuri and Mkhedruli) plus Mtavruli
 * (U+1C90–U+1CBF). Wider than the Mkhedruli range the app actually renders, on purpose: the
 * question this answers is "is the learner typing Georgian at all", and a learner whose keyboard
 * produces Mtavruli capitals is emphatically not someone to interrupt with "not typing Georgian
 * right now?".
 */
export function isGeorgianChar(ch: string): boolean {
  const cp = ch.codePointAt(0);
  if (cp === undefined) return false;
  return (cp >= 0x10a0 && cp <= 0x10ff) || (cp >= 0x1c90 && cp <= 0x1cbf);
}

/**
 * The first keystroke of an answer. Returns the state unchanged unless this is the moment to
 * offer options: the learner is typing, it is not Georgian, and we have not asked before.
 *
 * Whitespace and punctuation are not evidence either way — a learner reaching for a space bar
 * has not told us what alphabet they are in — so only a letter or digit can trigger the offer.
 */
export function keystroke(s: RunState, ch: string): RunState {
  if (s.offerState !== 'unoffered' || s.answerMode === 'options') return s;
  if (!/[\p{L}\p{N}]/u.test(ch)) return s;
  if (isGeorgianChar(ch)) {
    // They ARE typing Georgian. Settle the offer rather than leave it armed: asking two prompts
    // later, after they have plainly been typing Georgian all along, would be worse than never.
    return { ...s, offerState: 'settled' };
  }
  return { ...s, offerState: 'showing' };
}

/** "Answer with options" — the rest of the run is chips. */
export function acceptOptions(s: RunState): RunState {
  return { ...s, answerMode: 'options', offerState: 'settled' };
}

/** The offer dismissed. It is not made again, whatever the learner types next. */
export function dismissOptions(s: RunState): RunState {
  return { ...s, offerState: 'settled' };
}

/**
 * A graded answer came back. Idempotent per prompt: a double-tap on Submit, or a retry after a
 * flaky response, must not record the same prompt twice — the summary would then say "7
 * reviewed" for a run of six.
 */
export function answered(
  s: RunState,
  verdict: { correct: boolean; graded?: boolean; expected: unknown; explanation?: string },
  mode: RunResult['mode'],
): RunState {
  const ex = currentExercise(s);
  if (!ex) return s;
  if (s.results.some((r) => r.exerciseId === ex.id)) return s;
  // Absent means graded, which is what every kind but listen_repeat is: a response from a worker
  // deployed before `graded` existed should behave as it did, not silently unscore the run.
  const graded = verdict.graded !== false;
  return {
    ...s,
    verdict: { ...verdict, graded },
    results: [...s.results, { exerciseId: ex.id, correct: verdict.correct, mode, graded }],
  };
}

/**
 * Skip. Emits nothing — F11 is explicit that the client does not call `exerciseAnswer` at all —
 * so the prompt is recorded here only so the run's own count is honest, and `correct` is false
 * because nothing was retrieved.
 */
export function skip(s: RunState): RunState {
  const ex = currentExercise(s);
  if (!ex) return s;
  const already = s.results.some((r) => r.exerciseId === ex.id);
  return {
    ...s,
    verdict: null,
    idx: s.idx + 1,
    results: already
      ? s.results
      : [...s.results, { exerciseId: ex.id, correct: false, mode: 'skipped', graded: false }],
  };
}

/** Move on. Clears the verdict so the next prompt does not flash the last one's answer. */
export function next(s: RunState): RunState {
  return { ...s, idx: s.idx + 1, verdict: null };
}

export interface RunSummary {
  /** prompts the MODEL scored — the ones that moved a memory state */
  reviewed: number;
  right: number;
  /** wrong + shown — what the learner will see again tomorrow */
  again: number;
  /** answered but not scored (listen & repeat). Practice, and the summary should say so. */
  practised: number;
}

/**
 * The run summary (`5e`): "{n} reviewed · {k} right · {m} to see again tomorrow".
 *
 * A skipped prompt is NOT reviewed. It emitted no event, the grader never heard about it, and
 * counting it would make the line disagree with what the app actually recorded — the one thing
 * this screen must never do.
 */
export function summarise(s: RunState): RunSummary {
  const answeredResults = s.results.filter((r) => r.mode !== 'skipped');
  const scored = answeredResults.filter((r) => r.graded);
  const right = scored.filter((r) => r.correct).length;
  return {
    reviewed: scored.length,
    right,
    again: scored.length - right,
    practised: answeredResults.length - scored.length,
  };
}

/**
 * The upper bound `ExerciseAnswerSchema` puts on `latencyMs`.
 *
 * Both renderers measured the dwell as `Date.now() - shownAt` with no ceiling, and the schema
 * caps it at an hour — so a prompt left on screen while the PWA sat in the background came back
 * PERMANENTLY unanswerable: the answer 422s, the retry sends a bigger number, and every retry
 * after that fails harder. Only Skip escaped, and Skip emits nothing, so the attempt was lost.
 *
 * Clamping belongs at the producer, not the boundary. Latency is only an input to the grader's
 * z-score, and a dwell past an hour is off that scale whatever the exact number: the learner
 * walked away. Recording "an hour or more" is the honest reading of it, and it is one the server
 * will accept.
 */
export const ANSWER_MAX_LATENCY_MS = 60 * 60 * 1000;

/** The dwell to report, clamped into what the server accepts. */
export function answerLatencyMs(shownAtMs: number, nowMs: number): number {
  const dwell = nowMs - shownAtMs;
  if (!Number.isFinite(dwell)) return 0;
  return Math.min(ANSWER_MAX_LATENCY_MS, Math.max(0, Math.round(dwell)));
}

/**
 * The answer to SHOW, for a prompt the learner got wrong or asked to see.
 *
 * `ExerciseAnswerResponse.expected` is `unknown` because its shape follows the kind, and for
 * `comprehension` it is the option INDEX — `grade()` returns `expected.answerIdx`. Rendering that
 * with `String()` would print "2" at a learner, so Review suppressed the line entirely for
 * comprehension and printed a dangling "The answer was" with nothing after it, while Read showed
 * nothing at all and left "Show answer" with no answer to show.
 *
 * The index is sound, it just needs resolving: `buildComprehension` stores `options` and
 * `answerIdx` in the same order and nothing shuffles them, so the option at that index IS the
 * answer. Out of range or absent, this returns null and the caller renders no line rather than
 * an empty one.
 */
export function expectedText(
  ex: { kind: string; prompt?: { options?: string[] } },
  expected: unknown,
): string | null {
  if (ex.kind === 'comprehension') {
    const idx = typeof expected === 'number' ? expected : Number.parseInt(String(expected), 10);
    const options = ex.prompt?.options ?? [];
    return Number.isInteger(idx) && idx >= 0 && idx < options.length
      ? (options[idx] ?? null)
      : null;
  }
  if (typeof expected === 'string') return expected.length > 0 ? expected : null;
  return expected === null || expected === undefined ? null : String(expected);
}

/**
 * The summary line (`5e`), as a string, so the copy is testable rather than buried in JSX.
 *
 * The rule it exists to keep: the line must never claim more than the database recorded. A run of
 * six listen & repeat prompts wrote six `retrieval_attempt` rows with `correct = null`, and the
 * old line read "6 reviewed · 6 right" — six passes the model never awarded, on an axis it was
 * never told about. Practice is worth counting; it is not worth counting as knowing.
 */
export function summaryLine(s: RunSummary): string {
  const parts: string[] = [];
  if (s.reviewed > 0 || s.practised === 0) {
    parts.push(`${s.reviewed} reviewed`, `${s.right} right`);
    if (s.again > 0) parts.push(`${s.again} to see again tomorrow`);
  }
  if (s.practised > 0) parts.push(`${s.practised} practised`);
  return parts.join(' · ');
}

/**
 * Swap the prompts the learner has not reached yet.
 *
 * "Answer with options" promises the REST of the run as chips, and the rest of the run is prompts
 * that do not exist in that shape yet — the server builds a `choice` only when asked. So accepting
 * has to fetch replacements, and this is where they land. Answered prompts are keyed by exercise
 * id in `results`, and none of those ids are touched, so the summary is unaffected.
 */
export function replaceTail(s: RunState, exercises: Exercise[]): RunState {
  if (exercises.length === 0) return s;
  return { ...s, exercises: [...s.exercises.slice(0, s.idx), ...exercises] };
}

/** `ExerciseNextSchema` caps `itemIds` at 50 — exceeding it is a 422, not a truncation. */
export const EXERCISE_NEXT_ITEM_IDS_MAX = 50;
/** How many due words a scoped run asks for (brief step 8: `count: min(8, due)`). */
export const SCOPED_RUN_MAX = 8;

/**
 * The due items to scope a run to, from a `videoItems` response.
 *
 * The slice is load-bearing. A whole episode's due list is unbounded — `loadSpanItems` has no
 * LIMIT — and the schema refuses more than 50 ids, so "Words in this episode" on a well-watched
 * episode 422'd every time, permanently, and surfaced as "It'll be here when the connection is."
 * The server only ever considers `count * 3` candidates, and `videoItems` returns them due-first,
 * so the ones kept are the ones that would have been used.
 */
export function scopedItemIds(
  items: ReadonlyArray<{ itemId: string; due: boolean }>,
): { itemIds: string[]; count: number } | null {
  const itemIds = items
    .filter((i) => i.due)
    .map((i) => i.itemId)
    .slice(0, Math.min(SCOPED_RUN_MAX * 3, EXERCISE_NEXT_ITEM_IDS_MAX));
  if (itemIds.length === 0) return null;
  return { itemIds, count: Math.min(SCOPED_RUN_MAX, itemIds.length) };
}
